import Flickity from 'flickity';

const storyCarousel = class extends HTMLElement {
  constructor() {
    super();

    this.slider = this.querySelector('[data-story-carousel]');
    this.initVars();
    this.paused = false;

    this.init();
  }

  initVars () {
    this.slides = this.querySelectorAll('[data-story-slide]');
    this.allProgressIndicators = this.querySelectorAll('[data-social-overlay-progress]');
    this.videos = this.querySelectorAll('[data-story-video]');
    this.videoPlayButtons = this.querySelectorAll('[data-story-video-play]');
    this.videoPauseButtons = this.querySelectorAll('[data-story-video-pause]');
    this.videoUnmuteButtons = this.querySelectorAll('[data-story-video-unmute]');
    this.videoMuteButtons = this.querySelectorAll('[data-story-video-mute]');
    this.videoReplayButtons = this.querySelectorAll('[data-story-video-replay]');
    this.allStories = this.querySelectorAll('[data-story-page]');
    this.prevButtons = this.querySelectorAll('[data-story-back]');
    this.nextButtons = this.querySelectorAll('[data-story-next]');
    this.carouselPrevious = this.querySelector('[data-carousel-previous]');
    this.carouselNext = this.querySelector('[data-carousel-next]');
  }

  /**
   * Initializes the story carousel
   * @returns true if successful
   */
  init() {
    const options = {
      wrapAround: true,
      pageDots: false,
      contain: true,
      on: {
        ready: () => {
          this.beginSlideTimer(0);
        },
      },
    };

    const slideWidth = 280;
    let slidesWidth = slideWidth * this.slides.length;

    do {
      this.slider.innerHTML = this.slider.innerHTML + this.slider.innerHTML;
      this.initVars();
      slidesWidth = slideWidth * this.slides.length;
    } while (window.innerWidth > slidesWidth);

    this.flickity = new Flickity(this.slider, options);
    this.flickity.on('change', (index) => {
      this.stopVideos();
      this.resetSlides();
      this.beginSlideTimer(index);
    });

    this.carouselPrevious.addEventListener('click', () => this.flickity.previous());
    this.carouselNext.addEventListener('click', () => this.flickity.next());

    this.slides.forEach(slide => {
      slide.addEventListener('click', (event) => {
        if (slide.classList.contains('is-selected') || event.target.localName === 'button') { return; }
        this.flickity.selectCell([...this.slides].findIndex(el => el === slide));
      });
    });

    this.slider.addEventListener('mouseenter', () => {
      this.paused = true;
    });

    this.slider.addEventListener('mouseleave', () => {
      this.paused = false;
    });

    this.prevButtons.forEach(button => {
      button.addEventListener('click', () => {
        this.goPrevious(button);
      });
    });

    this.nextButtons.forEach(button => {
      button.addEventListener('click', () => {
        this.goNext(button);
      });
    });

    this.allProgressIndicators.forEach(indicator => {
      indicator.addEventListener('click', () => {
        const slide = indicator.closest('[data-story-slide]');

        if (!slide.classList.contains('is-selected')) { return; }

        const indicatorIndex = indicator.getAttribute('data-social-overlay-progress');
        const activeIndicatorIndex = Number(slide.dataset.index);

        if (indicatorIndex > activeIndicatorIndex) {
          clearInterval(this.interval);
          this.beginSlideTimer([...this.slides].findIndex(el => el === slide), indicatorIndex);
        } else if (indicatorIndex < activeIndicatorIndex) {
          this.resetSlides();
          this.beginSlideTimer([...this.slides].findIndex(el => el === slide), indicatorIndex);
        }

        setTimeout(() => indicator.classList.add('active'), 10);
      });
    });

    this.videoPlayButtons.forEach((button) => {
      button.addEventListener('click', () => {
        const storySlide = button.closest('[data-story-slide]');
        const video = storySlide.querySelector('[data-story-video]');
        storySlide.classList.remove('story-slide--paused');
        video.play();
        this.paused = false;
      });
    });

    this.videoPauseButtons.forEach((button) => {
      button.addEventListener('click', () => {
        const storySlide = button.closest('[data-story-slide]');
        const video = storySlide.querySelector('[data-story-video]');
        storySlide.classList.add('story-slide--paused');
        video.pause();
        this.paused = true;
      });
    });

    this.videoUnmuteButtons.forEach((button) => {
      button.addEventListener('click', () => {
        const storySlide = button.closest('[data-story-slide]');
        const video = storySlide.querySelector('[data-story-video]');
        storySlide.classList.add('story-slide--muted');
        video.muted = false;
      });
    });

    this.videoMuteButtons.forEach((button) => {
      button.addEventListener('click', () => {
        const storySlide = button.closest('[data-story-slide]');
        const video = storySlide.querySelector('[data-story-video]');
        storySlide.classList.remove('story-slide--muted');
        video.muted = true;
      });
    });

    this.videoReplayButtons.forEach((button) => {
      button.addEventListener('click', () => {
        this.resetSlides();
        this.beginSlideTimer(0, 0);
      });
    });
  }

  stopVideos() {
    this.videos.forEach((video) => {
      video.pause();
    });
  }

  resetSlides () {
    clearInterval(this.interval);

    this.slides.forEach(slide => {
      slide.setAttribute('data-index', 0);
    });

    this.allStories.forEach(story => {
      story.classList.remove('active');
    });

    this.allProgressIndicators.forEach(indicator => {
      // Reset indicators
      indicator.classList.remove('active');
      indicator.classList.remove('skipped');
    });
  }

  beginSlideTimer (slideIndex, startingIndex = 0) {
    const slide = this.slides[slideIndex];
    const storyPages = slide.querySelectorAll('[data-story-page]');
    const indicators = slide.querySelectorAll('[data-social-overlay-progress]');
    let storyIndex = startingIndex;
    const storyVideo = this.slides[slideIndex].querySelector('[data-story-video]');
    const storyVideoLength = storyVideo ? parseInt(storyVideo.getAttribute('data-story-video-length')) : null;
    const slideLength = storyVideoLength ? (storyVideoLength * 1000) : 5000;

    if (storyVideo) {
      slide.classList.remove('story-slide--paused');
      storyVideo.load();
      storyVideo.play();
    }

    if (storyPages[storyIndex]) {
      storyPages[storyIndex].classList.add('active');
    }

    indicators.forEach((indicator, index) => {
      indicator.classList.remove('active');
      setTimeout(() => {
        indicator.classList.toggle('skipped', index < startingIndex);
        indicator.classList.toggle('active', index === startingIndex);
      }, 10);
    });

    slide.setAttribute('data-index', storyIndex);

    this.interval = setInterval(() => {
      storyIndex++;
      slide.setAttribute('data-index', storyIndex);

      if (storyPages.length <= storyIndex) {
        if (this.paused) { return; }

        this.stopVideos();
        clearInterval(this.interval);
        this.flickity.next();
        return;
      }

      indicators[storyIndex].classList.add('active');

      storyPages.forEach((storyPage, index) => {
        storyPage.classList.toggle('active', index === storyIndex);
      });

      indicators[storyIndex].classList.add('active');
    }, slideLength);
  }

  goPrevious (button) {
    const slide = button.closest('[data-story-slide]');

    if (!slide.classList.contains('is-selected')) { return; }

    const activeIndicatorIndex = Number(slide.dataset.index);

    if (activeIndicatorIndex === 0) {
      this.flickity.previous();
      return;
    }

    this.resetSlides();
    this.beginSlideTimer([...this.slides].findIndex(el => el === slide), (activeIndicatorIndex - 1));
  }

  goNext (button) {
    const slide = button.closest('[data-story-slide]');

    if (!slide.classList.contains('is-selected')) { return; }

    const activeIndicatorIndex = Number(slide.dataset.index);
    const storyPages = slide.querySelectorAll('[data-story-page]');

    if (activeIndicatorIndex === (storyPages.length - 1)) {
      this.flickity.next();
      return;
    }

    clearInterval(this.interval);

    this.beginSlideTimer([...this.slides].findIndex(el => el === slide), (activeIndicatorIndex + 1));
  }
};

if (typeof window.customElements.get('story-carousel') === 'undefined') {
  window.customElements.define('story-carousel', storyCarousel);
}
